import { IDetailsRowProps } from '@fluentui/react';
import {
  CommonStyles,
  defaultSemanticColors,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  FontWeights,
  IColumnWithDataType,
  IDetailsListProps,
  Link,
  linkStylesGhost,
  linkStylesWidgetPanel,
  ListRow,
  RouterLink,
  useToast,
  WidgetPanel,
  widgetPanelStylesNoBG,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { fsApi } from '../../featurestore/api';
import {
  ActivePermission,
  CoreService,
  FeatureSetPopularity,
  FeatureSetsPopularityResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { activePermissionNameMap, CommonImageSrc } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import { RequestPermissionPanel } from '../RequestPermissionPanel/RequestPermissionPanel';

const getColumns = (onRequestAccess: (id?: string, name?: string) => void): IColumnWithDataType[] => {
  return [
    {
      key: 'featureSetName',
      name: 'Feature Set Name',
      fieldName: 'featureSetDescription',
      minWidth: 200,
      data: {
        headerFieldName: 'featureSetName',
        listCellProps: {
          emptyMessage: 'No Description',
          onRenderPrefix: (item) =>
            item.featureSetName?.toLowerCase().includes('h2o') ? (
              <ListCellImage imageSrc={CommonImageSrc.logo512} />
            ) : (
              <ListCellImage iconName="FormLibraryMirrored" />
            ),
        },
      },
    },
    {
      key: 'numberOfRetrievals',
      name: 'Number Of Retrievals',
      fieldName: 'numberOfRetrievals',
      minWidth: 150,
    },
    {
      key: 'AccessType',
      name: 'Access Type',
      fieldName: 'permission',
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: FeatureSetPopularity) => {
        return item.permission !== ActivePermission.ACTIVE_PERMISSION_NONE ? (
          <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
            <div
              style={{
                fontWeight: FontWeights.semiBold,
                paddingBottom: 6,
              }}
            >
              Access Type
            </div>
            <div
              style={{
                height: 18,
                color: defaultSemanticColors.textSecondary,
                ...(CommonStyles.truncateString as CSSProperties),
              }}
            >
              {item.permission ? activePermissionNameMap.get(item.permission) : ''}
            </div>
          </div>
        ) : (
          <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
            <Link onClick={() => onRequestAccess(item.featureSetId, item.featureSetName)}>Request Access</Link>
          </div>
        );
      },
    },
    {
      key: 'view',
      name: 'View',
      minWidth: 120,
      onRender: (item: FeatureSetPopularity) => {
        const { featureSetId } = item;
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              width: '100%',
              textAlign: 'right',
            }}
          >
            <div>
              <RouterLink styles={linkStylesGhost} to={`${RoutePaths.FEATURESTORE_FEATURE_SET}/${featureSetId}`}>
                View
              </RouterLink>
            </div>
          </div>
        );
      },
    },
  ];
};

export const PopularFeatureSetListWidget = () => {
  const [data, setData] = useState<FeatureSetPopularity[]>([]);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showRequestAccessPanel, setShowRequestAccessPanel] = useState(false);
  const history = useHistory();
  const refMeta = useRef<{
    selectedIdName: { id: string; name: string };
  }>({
    selectedIdName: { id: '', name: '' },
  });
  const { addToast } = useToast();
  const load = useCallback(async () => {
    setLoadingMessage(`Loading popular feature sets...`);
    try {
      const { featureSets = [] }: FeatureSetsPopularityResponse = await fsApi(CoreService.GetFeatureSetsPopularity);
      setData(featureSets);
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while loading the popular feature sets: `));
    } finally {
      setLoadingMessage('');
    }
  }, [addToast]);
  const onRequestAccess = useCallback((id = '', name = '') => {
    refMeta.current.selectedIdName = { id, name };
    setShowRequestAccessPanel(true);
  }, []);
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    columns: getColumns(onRequestAccess),
    items: data,
    isHeaderVisible: false,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return <ListRow data={item} columns={columns} />;
    },
  };
  useEffect(() => {
    load();
  }, []);
  return (
    <>
      <WidgetPanel
        data-test="popular-feature-set-list-widget"
        styles={widgetPanelStylesNoBG}
        title="Most Popular"
        loadingMessage={loadingMessage}
        emptyMessageProps={
          !loadingMessage && data.length === 0
            ? {
                message: `You don't have any popular feature sets.`,
              }
            : undefined
        }
        linkStyles={linkStylesWidgetPanel}
        linkText="View all"
        linkOnClick={(e) => {
          history.push(RoutePaths.FEATURESTORE_FEATURE_SETS);
          e?.preventDefault();
        }}
      >
        <div style={{ height: '100%', overflow: 'auto' }}>
          <DetailsList {...detailsListProps} />
        </div>
      </WidgetPanel>
      <RequestPermissionPanel
        featureSetId={refMeta.current.selectedIdName.id}
        name={refMeta.current.selectedIdName.name}
        show={showRequestAccessPanel}
        onDismiss={(_e, updated) => {
          if (updated) {
            load();
          }
          setShowRequestAccessPanel(false);
        }}
      />
    </>
  );
};
